import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"
import { validateLeadPhoneFormat } from "../../lib/phoneFormat"
import { intakeApi, IntakeQuestion } from "../../services/intakeApi"
import { ApiError } from "../../services/apiMiddleware"
import IntakeHeader from "./IntakeHeader"

const LINK_ERROR = "This link is invalid or has expired."

export default function IntakeForm() {
  const [params] = useSearchParams()
  const token = params.get("token") || ""
  const navigate = useNavigate()
  const [questions, setQuestions] = useState<IntakeQuestion[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [pageError, setPageError] = useState("")

  useEffect(() => {
    if (!token) {
      setPageError(LINK_ERROR)
      setLoading(false)
      return
    }
    intakeApi.getForm(token)
      .then((payload) => {
        setQuestions(payload.questions)
        setAnswers({
          first_name: payload.prefill.first_name || "",
          last_name: payload.prefill.last_name || "",
          phone: payload.prefill.phone || "",
          email: payload.prefill.email || "",
        })
      })
      .catch((error: ApiError) => {
        setPageError(error.message || LINK_ERROR)
      })
      .finally(() => setLoading(false))
  }, [token])

  const setAnswer = (key: string, value: string) => {
    setAnswers((current) => ({ ...current, [key]: value }))
  }

  const validate = () => {
    const next: Record<string, string> = {}
    questions.forEach((question) => {
      const value = (answers[question.key] || "").trim()
      if (question.required && !value) {
        next[question.key] = "This field is required."
        return
      }
      if (question.key === "phone" && value) {
        const phoneError = validateLeadPhoneFormat(value)
        if (phoneError) next[question.key] = phoneError
      }
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      await intakeApi.submit(token, answers)
      navigate("/intake/success", { replace: true })
    } catch (error) {
      const apiError = error as ApiError
      const fields = apiError.data?.fields as Record<string, string[]> | undefined
      if (fields) {
        const next: Record<string, string> = {}
        Object.entries(fields).forEach(([key, messages]) => {
          next[key] = messages[0]
        })
        setErrors(next)
      } else {
        setPageError(apiError.message || LINK_ERROR)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <IntakeHeader />
        <p className="p-6 text-center text-sm text-slate-600">Loading intake form…</p>
      </div>
    )
  }

  if (pageError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <IntakeHeader />
        <p role="alert" className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-700">
          {pageError}
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <IntakeHeader />
      <div className="mx-auto max-w-xl px-4 pb-6">
        <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          {questions.map((question) => (
            <QuestionField
              key={question.key}
              question={question}
              value={answers[question.key] || ""}
              error={errors[question.key]}
              onChange={(value) => setAnswer(question.key, value)}
            />
          ))}
          <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800">
            Submit
          </Button>
        </form>
      </div>
    </div>
  )
}

type QuestionFieldProps = Readonly<{
  question: IntakeQuestion
  value: string
  error?: string
  onChange: (value: string) => void
}>

const YES_NO_OPTIONS = ["yes", "no"] as const

function inputType(key: string): "email" | "tel" | "text" {
  if (key === "email") return "email"
  if (key === "phone") return "tel"
  return "text"
}

function QuestionField({ question, value, error, onChange }: QuestionFieldProps) {
  if (question.kind === "yes_no") {
    return (
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-slate-900">{question.label}</legend>
        <div className="flex gap-2">
          {YES_NO_OPTIONS.map((option) => (
            <label
              key={option}
              className={`relative min-w-[4.5rem] cursor-pointer rounded-md border px-4 py-2 text-center text-sm font-medium ${
                value === option ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <input
                type="radio"
                name={question.key}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              {option === "yes" ? "Yes" : "No"}
            </label>
          ))}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </fieldset>
    )
  }

  const inputId = `intake-${question.key}`
  return (
    <div>
      <Label htmlFor={inputId}>{question.label}</Label>
      {question.kind === "textarea" ? (
        <Textarea id={inputId} value={value} onChange={(event) => onChange(event.target.value)} rows={6} className="mt-1" />
      ) : (
        <Input
          id={inputId}
          type={inputType(question.key)}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1"
        />
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}
